import React, {useCallback, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import {useAuth} from "hooks/useAuth";
import MessagesContent from "features/notifications/components/MessageContent";
import MessageTitle from "features/notifications/components/MessageTitle";
import BoxContent from "shared/ui/BoxContent";
import CustomAccordion from "shared/ui/CustomAccordion";
import Loading from "shared/ui/Loading";
import PageTitle from "shared/ui/PageTitle";
import {
    APIPostNotifications,
    APIPostNotificationsMarkAsRead,
    APIPostNotificationsUnread,
} from "features/notifications/api/notifications";

interface IDAccordionData {
    date?: Date;
    title: JSX.Element;
    content: JSX.Element;
    status?: string;
    id: string;
    mode: string;
    bgColor: string;
}

interface IDPagination {
    currentPage: number;
    count: number;
    rowsPerPage: number;
}

const getNotificationType = (type: string): string => {
    switch (type) {
        case "Domain\\Notifications\\Notifications\\TestSessionClosedNotification": return "TEST_SESSION_CLOSED";
        case "Domain\\Notifications\\Notifications\\TestSessionExpiredNotification": return "TEST_SESSION_EXPIRED";
        case "Domain\\Notifications\\Notifications\\TestSessionStartedNotification": return "TEST_SESSION_STARTED";
        case "Domain\\Notifications\\Notifications\\TestSessionCreatedNotification": return "TEST_SESSION_CREATED";
        case "Domain\\Notifications\\Notifications\\ExportNotification": return "EXPORT_CREATED";
        default: return "USER_CREATED";
    }
};

const getNotificationColor = (type: string): string => {
    switch (type) {
        case "Domain\\Notifications\\Notifications\\TestSessionClosedNotification": return "success.dark";
        case "Domain\\Notifications\\Notifications\\TestSessionExpiredNotification": return "error.dark";
        case "Domain\\Notifications\\Notifications\\TestSessionStartedNotification": return "warning.dark";
        default: return "background.paper";
    }
};

const Messages = () => {
    const {update, user} = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [messages, setMessages] = useState<IDAccordionData[]>([]);
    const [pagination, setPagination] = useState<IDPagination>({currentPage: 0, count: 0, rowsPerPage: 30});
    const [ready, setReady] = useState(false);
    const prevUserNotifications = useRef<number>();

    const mapNotificationData = useCallback((item: any, position: string): IDAccordionData => {
        const name = position === "student" ? item.test_owner_name : item.test_tester_name;
        const notificationType = getNotificationType(item.type);
        const testMode = notificationType === "EXPORT_CREATED" ? item.test_mode_name : item.test_mode_id;
        const file = notificationType === "EXPORT_CREATED" ? {fileName: item.file_name, filePath: item.file_path} : null;
        return {
            id: item.id,
            mode: item.test_mode_id,
            bgColor: getNotificationColor(item.type),
            date: item.created_at,
            status: item.read_at !== null ? "citit" : "necitit",
            title: (
                <MessageTitle
                    subject={item.test_item_type}
                    name={name}
                    classroom={item.test_classroom_name}
                    classroom_grade={item.test_classroom_grade}
                    mode={testMode}
                    type={notificationType}
                    file={file}
                />
            ),
            content: (
                <MessagesContent
                    createdAt={item.test_created_at}
                    subject={item.test_item_type}
                    expires={item.test_expires_at}
                    id={item.test_id}
                    grade={item.test_grade}
                    mode={testMode}
                    classroom={item.test_classroom_name}
                    classroom_grade={item.test_classroom_grade}
                    classroom_id={item.test_classroom_id}
                    name={name}
                    type={notificationType}
                    startsAt={item.test_starts_at}
                    file={file}
                />
            ),
        };
    }, []);

    const fetchNotifications = useCallback(() => {
        setLoading(true);
        APIPostNotifications(`?page=${pagination.currentPage + 1}`)
            .then((response) => {
                if (response.data.success) {
                    const { data: rawData } = response.data;
                    setPagination({
                        currentPage: rawData.current_page - 1,
                        count: rawData.total,
                        rowsPerPage: rawData.per_page
                    });

                    const receivedMessages = rawData?.data?.map((item: any) =>
                        mapNotificationData(item, user.position)
                    ) || [];

                    setMessages(receivedMessages);
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(() => update("error", { show: true, code: "A001" }))
            .finally(() => setLoading(false));
    }, [pagination.currentPage, mapNotificationData, user.position, update]);

    const fetchUnreadNotificationsNumber = useCallback(() => {
        APIPostNotificationsUnread()
            .then((response) => {
                if (response.data.success) {
                    update("user", {notifications: response.data.data.value});
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("getMessages ERR", err);
                update("error", {show: true, code: "A001"});
            });
    }, [update])

    useEffect(() => {
        setReady(true);
    }, []);

    useEffect(() => {
        if (!ready) return;
        if (prevUserNotifications.current === undefined) {
            prevUserNotifications.current = user.notifications;
        }
        if (user.notifications < prevUserNotifications.current) return;

        fetchUnreadNotificationsNumber();
        fetchNotifications();

        prevUserNotifications.current = user.notifications;
    }, [user.notifications, ready, pagination.currentPage]);

    useEffect(() => {
        setLoading(true);
        fetchNotifications();
    }, [pagination.currentPage]);

    const markedAsRead = (id: string) => {
        APIPostNotificationsMarkAsRead({notification_id: id, page: pagination.currentPage + 1})
            .then((response) => {
                if (response.data.success) {
                    const markAsRead = messages.map((item) => {
                        if (item.id === id) {
                            return {...item, status: "citit"};
                        }
                        return item;
                    });
                    setMessages(markAsRead);
                    fetchUnreadNotificationsNumber();
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                update("error", {show: true, code: "A001"});
            });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setMessages([]);
        setPagination({...pagination, currentPage: newPage});
    };

    return (
        <Box component="main">
            <>
                <PageTitle>Mesaje</PageTitle>
                {loading ? (
                    <Box mt={3} display="flex" alignItems="center" width="100%">
                        <Loading show={true}/>
                    </Box>
                ) : messages.length === 0 ? (
                    <BoxContent>
                        <Typography variant="body1">Nu sunt mesaje.</Typography>
                    </BoxContent>
                ) : (
                    <>
                        <CustomAccordion data={messages} returnFunction={markedAsRead}/>
                        <TablePagination
                            rowsPerPageOptions={[30]}
                            component="div"
                            count={pagination.count}
                            rowsPerPage={pagination.rowsPerPage}
                            labelDisplayedRows={function defaultLabelDisplayedRows({from, to, count}) {
                                return `${from}–${to} din ${count !== -1 ? count : `mai multe de ${to}`}`;
                            }}
                            page={pagination.currentPage}
                            onPageChange={handleChangePage}
                        />
                    </>
                )}
            </>
        </Box>
    );
};

export default Messages;
